// 담당: 혜원 · Financial — 매출(마케팅+커머스) + 비즈니스 캘린더
// KPI·매출 추이·프로젝트 목록은 실 API(fetchFinancial, dev 실패 시 mock 폴백).
// 비즈니스 캘린더는 스케줄 데이터라 이 엔드포인트와 별개 — 아직 정적(후속 연동).
import { useEffect, useRef, useState } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { fetchCampaignBoard, fetchFinancial, type CampaignScheduleEvent, type FinancialData } from '../creatorsApi'

let CREATOR_ID = '0012w00000n4rUJAAY' // 셸 라우트(creatorId)로 세팅됨. 없으면 스캐폴드 기본(레오제이).

// 원 → "₩1.82억" (백만 단위 반올림 후 소수 2자리 — 1.355 부동소수점 내림 방지)
const eok = (won: number) => '₩' + (Math.round(won / 1e6) / 100).toFixed(2) + '억'

// 비즈니스 캘린더 — Campaigns와 동일한 SF 일정(촬영/업로드/라이브/마감) 사용.
const WEEKDAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S']
const MONTH_EN = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
const TAG_COLOR: Record<string, string> = { 촬영: '#3E3BE0', 편집: '#3E3BE0', 업로드: '#5653FA', 라이브: '#C84BC9', 마감: '#B4453C' }
const tagColor = (t: string) => TAG_COLOR[t] ?? '#5653FA'

// ── 막대 차트 좌표 (viewBox 0 0 460 160) ───────────────
const CVB_W = 460, CVB_H = 160
const CHART_L = 30, CHART_R = 440, CBASE = 138, CTOP = 34
const pct = (n: number, whole: number) => `${(n / whole) * 100}%`

// ── 기간 필터 (클라이언트 재집계) ──────────────────────
type Period = 'all' | 'year' | 'quarter' | 'month'
const PERIODS: { key: Period; label: string }[] = [
  { key: 'all', label: '전체' },
  { key: 'year', label: '연' },
  { key: 'quarter', label: '분기' },
  { key: 'month', label: '월' },
]
const brandOf = (project: string) => project.split('_')[1] || project.split('_')[0] || project
const ymOf = (d: string): [number, number] => {
  const [y, m] = d.split('.').map(Number)
  return [y || 0, m || 0]
}
function inPeriod(d: string, period: Period, ry: number, rm: number): boolean {
  if (period === 'all') return true
  const [y, m] = ymOf(d)
  if (period === 'year') return y === ry
  if (period === 'month') return y === ry && m === rm
  return y === ry && Math.floor((m - 1) / 3) === Math.floor((rm - 1) / 3)
}
function deriveFinancial(data: FinancialData, period: Period) {
  // 기준 시점 = 오늘 (연/분기/월 모두 실제 오늘 기준).
  // 단, 일정 널뛰기로 현재 기간이 비어있으면 데이터가 있는 직전 기간으로 폴백.
  const now = new Date()
  let ry = now.getFullYear()
  let rm = now.getMonth() + 1
  if (period !== 'all') {
    const hasData = (yy: number, mm: number) => data.perProject.some((p) => inPeriod(p.date, period, yy, mm))
    let guard = 0
    while (!hasData(ry, rm) && guard < 120) {
      if (period === 'year') ry -= 1
      else if (period === 'month') { rm -= 1; if (rm < 1) { rm = 12; ry -= 1 } }
      else { rm -= 3; if (rm < 1) { rm += 12; ry -= 1 } } // 분기: 3개월 전
      guard++
    }
  }
  const periodLabel =
    period === 'month' ? `${ry}.${String(rm).padStart(2, '0')}`
    : period === 'quarter' ? `${ry} Q${Math.floor((rm - 1) / 3) + 1}`
    : period === 'year' ? `${ry}년`
    : ''
  const proj = data.perProject.filter((p) => inPeriod(p.date, period, ry, rm))
  const totalGmv = proj.reduce((s, p) => s + p.gmv, 0)
  const avgGmv = proj.length ? Math.round(totalGmv / proj.length) : 0
  const top = proj.reduce<{ project: string; gmv: number } | null>((a, p) => (!a || p.gmv > a.gmv ? p : a), null)
  const topProject = top ? `${brandOf(top.project)} ${'₩' + (Math.round(top.gmv / 1e6) / 100).toFixed(2) + '억'}` : '—'
  // 차트 버킷 — 기간별로 다르게 집계
  //   전체 → 분기별 / 연·분기 → 월별 / 월 → 지난달·이번달
  const sumMonth = (yy: number, mm: number) =>
    data.perProject.filter((p) => { const [y, m] = ymOf(p.date); return y === yy && m === mm }).reduce((s, p) => s + p.gmv, 0)
  let chart: { label: string; gmv: number }[]
  let chartTitle: string
  if (period === 'month') {
    let pm = rm - 1
    let py = ry
    if (pm < 1) { pm = 12; py = ry - 1 }
    chart = [
      { label: `${String(py % 100).padStart(2, '0')}.${String(pm).padStart(2, '0')}`, gmv: sumMonth(py, pm) },
      { label: `${String(ry % 100).padStart(2, '0')}.${String(rm).padStart(2, '0')}`, gmv: sumMonth(ry, rm) },
    ]
    chartTitle = '매출 · 지난달 vs 이번달'
  } else if (period === 'quarter') {
    const cq = Math.floor((rm - 1) / 3)
    let pqy = ry
    let pq = cq - 1
    if (pq < 0) { pq = 3; pqy = ry - 1 }
    const sumQ = (yy: number, qq: number) =>
      data.perProject.filter((p) => { const [y, m] = ymOf(p.date); return y === yy && Math.floor((m - 1) / 3) === qq }).reduce((s, p) => s + p.gmv, 0)
    chart = [
      { label: `${String(pqy % 100).padStart(2, '0')} Q${pq + 1}`, gmv: sumQ(pqy, pq) },
      { label: `${String(ry % 100).padStart(2, '0')} Q${cq + 1}`, gmv: sumQ(ry, cq) },
    ]
    chartTitle = '매출 · 지난분기 vs 이번분기'
  } else if (period === 'year') {
    const byM = new Map<number, number>()
    for (const p of proj) { const [, m] = ymOf(p.date); byM.set(m, (byM.get(m) ?? 0) + p.gmv) }
    chart = [...byM.entries()].sort((a, b) => a[0] - b[0]).map(([m, gmv]) => ({ label: `${m}월`, gmv }))
    chartTitle = '월별 매출'
  } else {
    const byQ = new Map<string, number>()
    for (const p of proj) {
      const [y, m] = ymOf(p.date)
      const label = `${String(y % 100).padStart(2, '0')} Q${Math.floor((m - 1) / 3) + 1}`
      byQ.set(label, (byQ.get(label) ?? 0) + p.gmv)
    }
    chart = [...byQ.entries()].sort((a, b) => a[0].localeCompare(b[0])).map(([label, gmv]) => ({ label, gmv }))
    chartTitle = '분기별 매출 추이'
  }
  // 전기 대비 델타
  let deltaPct: number | null = null
  if (period !== 'all') {
    let py = ry
    let pm = rm
    if (period === 'year') py = ry - 1
    else if (period === 'month') {
      pm = rm - 1
      if (pm < 1) { pm = 12; py = ry - 1 }
    } else {
      const cq = Math.floor((rm - 1) / 3)
      if (cq === 0) { py = ry - 1; pm = 10 } else pm = cq * 3 - 2
    }
    const prev = data.perProject.filter((p) => inPeriod(p.date, period, py, pm)).reduce((s, p) => s + p.gmv, 0)
    if (prev > 0) deltaPct = Math.round(((totalGmv - prev) / prev) * 100)
  }
  return { totalGmv, avgGmv, topProject, chart, chartTitle, perProject: proj, deltaPct, count: proj.length, periodLabel }
}

export function FinancialSection({ creatorId }: { creatorId?: string }) {
  if (creatorId) CREATOR_ID = creatorId // 셸 라우트 creator_id 반영
  const [data, setData] = useState<FinancialData | null>(null)
  const [period, setPeriod] = useState<Period>('all')
  const [schedule, setSchedule] = useState<CampaignScheduleEvent[]>([])
  const now = new Date()
  const [calYm, setCalYm] = useState({ y: now.getFullYear(), m: now.getMonth() + 1 })
  // 캘린더 셀 크기에 따라 노출 이벤트 줄 수 자동 조절
  const calGridRef = useRef<HTMLDivElement>(null)
  const [chipRows, setChipRows] = useState(2)

  useEffect(() => {
    fetchFinancial(CREATOR_ID).then(setData).catch(() => setData(null))
    // 비즈니스 캘린더 = Campaigns와 동일 SF 일정 소스
    fetchCampaignBoard(CREATOR_ID).then((b) => setSchedule(b.schedule ?? [])).catch(() => setSchedule([]))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [creatorId])

  useEffect(() => {
    const el = calGridRef.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(() => {
      const cellH = el.clientWidth / 7 // aspect-square → 높이 ≈ 너비/7
      const avail = cellH - 26 - 8 // 날짜줄 + 패딩 여유
      setChipRows(Math.max(1, Math.min(4, Math.floor(avail / 16)))) // 칩 1줄 ≈ 16px
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [data])

  if (!data) {
    return <div className="p-8 text-sm text-fg-3">매출 불러오는 중…</div>
  }

  const view = deriveFinancial(data, period)
  const q = view.chart
  const n = Math.max(q.length, 1)
  const slot = (CHART_R - CHART_L) / n
  const barW = Math.min(66, slot * 0.62)
  const maxEok = Math.max(...q.map((d) => d.gmv / 1e8), 0.1)
  const cmax = maxEok / 0.82 // 가장 높은 막대가 ~82% 높이
  const minIdx = q.reduce((mi, d, i, a) => (d.gmv < a[mi].gmv ? i : mi), 0)
  const barX = (i: number) => CHART_L + slot * i + (slot - barW) / 2
  const barCx = (i: number) => barX(i) + barW / 2
  const barH = (gmv: number) => (gmv / 1e8 / cmax) * (CBASE - CTOP)

  // Business Calendar — 월 이동 + SF 일정(촬영/업로드/라이브/마감)
  const calKey = `${calYm.y}-${String(calYm.m).padStart(2, '0')}`
  const calEvents: Record<number, CampaignScheduleEvent[]> = {}
  for (const e of schedule) {
    if (!e.date.startsWith(calKey)) continue
    const day = Number(e.date.slice(8, 10))
    ;(calEvents[day] ??= []).push(e)
  }
  const calLead = new Date(calYm.y, calYm.m - 1, 1).getDay()
  const calDays = Array.from({ length: new Date(calYm.y, calYm.m, 0).getDate() }, (_, i) => i + 1)
  const moveCal = (delta: number) => {
    const d = new Date(calYm.y, calYm.m - 1 + delta, 1)
    setCalYm({ y: d.getFullYear(), m: d.getMonth() + 1 })
  }
  const calEntries = Object.entries(calEvents)
    .flatMap(([day, evs]) => evs.map((e) => ({ day: Number(day), ...e })))
    .sort((a, b) => a.day - b.day)

  return (
    <div className="space-y-6">
      {/* 헤더 + 기간 필터 */}
      <div className="flex items-end justify-between gap-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-widest text-fg-3">Financial · 매출</div>
          <h2 className="mt-2 font-display text-2xl font-bold text-fg-1">Revenue</h2>
        </div>
        <div className="flex items-center gap-2">
          {view.periodLabel && <span className="text-xs font-display tabular-nums text-fg-3">{view.periodLabel}</span>}
          <div className="flex items-center border border-line-hairline bg-surface-base">
            {PERIODS.map((p) => (
              <button
                key={p.key}
                onClick={() => setPeriod(p.key)}
                className={'px-3 py-1.5 text-xs font-medium ' + (period === p.key ? 'bg-ink-800 text-fg-inverse' : 'text-fg-3 hover:text-fg-1')}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* KPI 3종 — 헤어라인 구분선 (gap 1px + 배경) */}
      <div className="grid grid-cols-1 gap-px border border-line-hairline bg-line-hairline sm:grid-cols-3">
        <div className="bg-surface-base px-5 py-4">
          <div className="flex items-center gap-2 text-[11px] text-fg-3">
            {period === 'all' ? '누적 매출' : '매출'}
            {view.deltaPct != null && (
              <span className={'font-semibold ' + (view.deltaPct >= 0 ? 'text-status-success' : 'text-status-danger')}>
                {view.deltaPct >= 0 ? '▲' : '▼'} {Math.abs(view.deltaPct)}% <span className="font-normal text-fg-3">전기</span>
              </span>
            )}
          </div>
          <div className="mt-1.5 font-display text-[28px] font-bold tabular-nums text-fg-1">{eok(view.totalGmv)}</div>
          <div className="text-[10px] text-fg-3">프로젝트 {view.count}건</div>
        </div>
        <div className="bg-surface-base px-5 py-4">
          <div className="text-[11px] text-fg-3">프로젝트 평균 매출</div>
          <div className="mt-1.5 font-display text-[28px] font-bold tabular-nums text-fg-1">{eok(view.avgGmv)}</div>
        </div>
        <div className="bg-surface-base px-5 py-4">
          <div className="text-[11px] text-fg-3">최고 거래 프로젝트</div>
          <div className="mt-2 font-display text-[22px] font-bold text-brand-blue">{view.topProject}</div>
        </div>
      </div>

      {/* 2단: 좌 매출 추이+리스트 / 우 캘린더 */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-[1.1fr_1fr]">
        {/* 프로젝트 건당 매출 추이 */}
        <div className="border border-line-hairline bg-surface-base px-5 py-4">
          <div className="mb-2.5 text-[13px] font-medium text-fg-1">{view.chartTitle}</div>
          <div className="relative">
            <svg viewBox={`0 0 ${CVB_W} ${CVB_H}`} className="block h-auto w-full">
              <line x1={CHART_L} y1={CBASE} x2={CHART_R} y2={CBASE} stroke="var(--ink-200)" />
              {q.map((d, i) => (
                <rect
                  key={d.label}
                  x={barX(i)}
                  y={CBASE - barH(d.gmv)}
                  width={barW}
                  height={barH(d.gmv)}
                  fill={i === minIdx ? 'var(--blue-300)' : 'var(--blue-400)'}
                />
              ))}
            </svg>
            {/* 값·분기 라벨: SVG text 대신 HTML 오버레이 (디자인 시스템 규칙) */}
            {/* 분기 수가 많으면 값 라벨은 겹치므로 생략, 분기 라벨은 축소·간헐 표기 */}
            <div className="pointer-events-none absolute inset-0">
              {q.length <= 6 &&
                q.map((d, i) => (
                  <span
                    key={d.label}
                    className="absolute -translate-x-1/2 -translate-y-1/2 whitespace-nowrap font-display text-[12px] font-bold text-fg-1"
                    style={{ left: pct(barCx(i), CVB_W), top: pct(CBASE - barH(d.gmv) - 9, CVB_H) }}
                  >
                    {eok(d.gmv)}
                  </span>
                ))}
              {q.map((d, i) => {
                const step = q.length > 10 ? 2 : 1 // 많으면 격번으로만
                if (i % step !== 0 && i !== q.length - 1) return null
                return (
                  <span
                    key={'l' + d.label}
                    className={'absolute -translate-x-1/2 whitespace-nowrap text-fg-3 ' + (q.length > 8 ? 'text-[8px]' : 'text-[10px]')}
                    style={{ left: pct(barCx(i), CVB_W), top: pct(151, CVB_H) }}
                  >
                    {d.label}
                  </span>
                )
              })}
            </div>
          </div>
          {/* 프로젝트별 GMV 리스트 */}
          <div className="mt-2.5">
            {view.perProject.length === 0 ? (
              <div className="py-8 text-center text-xs text-fg-3">선택한 기간에 거래 내역이 없습니다.</div>
            ) : (
              view.perProject.map((r, i) => (
                <div key={r.project + '-' + r.date + '-' + i} className="flex items-center justify-between border-b border-line-hairline py-2.5 last:border-b-0">
                  <div>
                    <div className="text-[13px] text-fg-1">{r.project}</div>
                    <div className="text-[11px] text-fg-3">{r.date}</div>
                  </div>
                  <div className="font-display text-[15px] font-bold tabular-nums text-fg-1">{eok(r.gmv)}</div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Business Calendar (스케줄 — 후속 연동) */}
        <div className="border border-line-hairline bg-surface-base px-5 py-4">
          <div className="mb-3 flex items-center justify-between">
            <div className="text-[13px] font-medium text-fg-1">Business Calendar</div>
            <div className="flex items-center gap-1">
              <button onClick={() => moveCal(-1)} aria-label="이전 달" className="flex h-7 w-7 items-center justify-center border border-line-hairline text-fg-3 hover:text-fg-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-blue">
                <ChevronLeft className="h-4 w-4" aria-hidden />
              </button>
              <span className="w-[112px] text-center font-display text-[14px] font-medium text-fg-1">{MONTH_EN[calYm.m - 1]} {calYm.y}</span>
              <button onClick={() => moveCal(1)} aria-label="다음 달" className="flex h-7 w-7 items-center justify-center border border-line-hairline text-fg-3 hover:text-fg-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-blue">
                <ChevronRight className="h-4 w-4" aria-hidden />
              </button>
            </div>
          </div>
          <div ref={calGridRef} className="grid grid-cols-7 gap-1.5">
            {WEEKDAYS.map((w, i) => (
              <div
                key={i}
                className={
                  'pb-1 text-center text-[10px] font-semibold ' +
                  (i === 0 ? 'text-status-danger' : i === 6 ? 'text-brand-blue' : 'text-fg-3')
                }
              >
                {w}
              </div>
            ))}
            {Array.from({ length: calLead }).map((_, i) => <div key={'lead' + i} />)}
            {calDays.map((d) => {
              const evs = calEvents[d] ?? []
              const has = evs.length > 0
              const dow = (d - 1 + calLead) % 7
              const isToday = calYm.y === now.getFullYear() && calYm.m === now.getMonth() + 1 && d === now.getDate()
              return (
                <div key={d} className="group relative">
                  <div
                    className={
                      'flex aspect-square flex-col overflow-hidden border p-1.5 ' +
                      (has ? 'border-[rgba(86,83,250,0.4)] bg-[rgba(86,83,250,0.04)]' : 'border-line-hairline')
                    }
                  >
                    <span
                      className={
                        (isToday ? 'flex h-6 w-6 items-center justify-center rounded-full bg-brand-blue text-[11px] font-semibold font-display tabular-nums text-fg-inverse'
                          : 'text-[11px] font-display tabular-nums ' + (has ? 'font-bold text-fg-1' : dow === 0 ? 'text-status-danger' : dow === 6 ? 'text-brand-blue' : 'text-fg-2'))
                      }
                    >
                      {d}
                    </span>
                    {has && (() => {
                      // 셀 크기에 맞춰 노출 줄 수 조절, 나머지는 +N (전체는 호버 툴팁)
                      const shown = evs.length <= chipRows ? evs.length : Math.max(1, chipRows - 1)
                      const extra = evs.length - shown
                      return (
                        <div className="mt-auto flex flex-col gap-0.5">
                          {evs.slice(0, shown).map((e, i) => (
                            <div key={i} className="truncate px-1 py-0.5 text-[8px] font-semibold leading-tight text-fg-inverse" style={{ background: tagColor(e.tag) }}>
                              {e.brand}
                            </div>
                          ))}
                          {extra > 0 && <div className="px-1 text-[8px] font-semibold leading-none text-fg-3">+{extra}</div>}
                        </div>
                      )
                    })()}
                  </div>
                  {has && (
                    // 디자인 툴팁 — 호버 시 전체 일정 노출 (래퍼는 클립 안 함)
                    <div className="pointer-events-none absolute left-1/2 top-[calc(100%+4px)] z-30 hidden w-max max-w-[200px] -translate-x-1/2 group-hover:block">
                      <div className="border border-line-hairline bg-surface-base px-3 py-2 shadow-lg">
                        <div className="mb-1.5 text-[10px] font-semibold font-display tabular-nums text-fg-3">{calYm.m}.{d} · {evs.length}건</div>
                        <ul className="space-y-1">
                          {evs.map((e, i) => (
                            <li key={i} className="flex items-center gap-1.5 whitespace-nowrap text-[11px]">
                              <span className="h-1.5 w-1.5 flex-none" style={{ background: tagColor(e.tag) }} aria-hidden />
                              <span className="font-medium text-fg-1">{e.brand}</span>
                              <span className="text-fg-3">{e.label}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
          {/* 이달의 일정 — 캘린더 이벤트 요약 (label 노출) */}
          <div className="mt-4 border-t border-line-hairline pt-3">
            <div className="mb-2.5 text-[10px] font-semibold uppercase tracking-wider text-fg-3">이달의 일정</div>
            {calEntries.length === 0 ? (
              <div className="py-3 text-center text-[12px] text-fg-3">이 달 일정 없음</div>
            ) : (
              <ul className="space-y-2">
                {calEntries.map((e, i) => (
                  <li key={i} className="flex items-center gap-2.5 text-[12px]">
                    <span className="w-8 flex-none font-display tabular-nums text-fg-3">{calYm.m}.{e.day}</span>
                    <span className="h-1.5 w-1.5 flex-none" style={{ background: tagColor(e.tag) }} aria-hidden />
                    <span className="font-medium text-fg-1">{e.brand}</span>
                    <span className="text-fg-3">·</span>
                    <span className="text-fg-2">{e.label}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
